import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "law-firm-ppc-colorado",
  metaTitle: "Law Firm PPC & Google Ads in Colorado | Attorney Lead Gen",
  metaDescription:
    "Profitable Google Ads and Local Services Ads for Colorado law firms — PI, family law, criminal defense, and estate planning. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Law firm SEO", to: "/law-firm-seo-colorado" },
  ],
  currentLabel: "Law Firm PPC Colorado",
  eyebrow: "Law Firm PPC — Colorado",
  h1: (
    <>
      Law Firm <span className="text-brand-gold">PPC</span> in Colorado
    </>
  ),
  heroDescription:
    "Profitable attorney lead generation in one of the most expensive PPC markets in the country — built around your true cost-per-signed-case math.",
  intro:
    "Colorado legal PPC is unforgiving. CPCs of $80–$300 are routine, and most firms blow budgets without ever measuring cost-per-signed-case. We run Google Search, Local Services Ads, and case-type-specific campaigns built around your actual case economics — tracked end-to-end from click to consultation to signed retainer. No vanity metrics, no agency upsells, just profitable case flow.",
  definition: {
    term: "Law firm PPC in Colorado",
    body: "Law firm PPC is the practice of running Google Search Ads, Local Services Ads (LSAs), and case-type-specific campaigns to drive qualified consultations and signed cases for Colorado attorneys.",
  },
  whatWeDo: [
    { title: "Practice-area campaign builds", body: "Separate campaigns and landing pages for each practice area you take — PI, family law, criminal defense, estate planning. No mixed-intent campaigns." },
    { title: "Local Services Ads (LSA) management", body: "LSA setup, badge optimization, weak-lead disputes, and category tuning to maximize qualified attorney calls." },
    { title: "Conversion-tested landing pages", body: "Landing pages built specifically for each practice area, with real attorney bios, case-type explanations, and clear consultation paths." },
    { title: "Cost-per-signed-case tracking", body: "We track click → form fill → consultation booked → consultation kept → signed retainer so you see actual cost-per-case, not cost-per-click." },
    { title: "Bid adjustments by case value", body: "Higher bids on case types with higher case value (PI, complex divorce); lower bids on commodity intake." },
    { title: "Call tracking and recording", body: "Every PPC call recorded and scored so your intake team can improve and you can see which campaigns drive signed cases vs. tire-kickers." },
  ],
  whyThisMarket: [
    {
      heading: "Colorado legal CPCs are among the highest in the US",
      body: "PI keywords in Denver routinely run $200–$400 per click. The only way to make that math work is end-to-end tracking — knowing exactly what each click costs you in signed-case terms.",
    },
    {
      heading: "Most legal PPC accounts mix practice areas in one campaign",
      body: "We routinely audit Colorado law firm accounts running PI, family, and criminal in a single campaign. Each practice area has different CPCs, different intent, and different conversion paths — they need separate everything.",
    },
    {
      heading: "Intake quality kills more legal PPC accounts than ad quality",
      body: "A great PPC campaign with bad intake wastes 60% of leads. We coordinate with your intake team and recommend changes when call recordings show drop-off patterns — because the agency that won't watch intake is the agency burning your budget.",
    },
  ],
  relatedLinks: [
    { to: "/law-firm-seo-colorado", label: "Law firm SEO in Colorado — full hub" },
    { to: "/colorado-springs-ppc", label: "How our Google Ads process works" },
    { to: "/law-firm-google-maps-colorado", label: "Law firm Google Maps optimization" },
    { to: "/colorado-springs-ppc", label: "Colorado Springs PPC" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado law firm case studies" },
    { to: "/google-ads-ppc", label: "Google Ads / PPC service (all industries)" },
    { to: "/pricing", label: "Pricing" },
  ],
  faqs: [
    { q: "What's a reasonable Colorado law firm PPC budget?", a: "Most Colorado firms run $5K–$30K/month depending on practice area and market. PI in Denver typically requires $15K+ to compete; family law in smaller markets can be productive at $3K–$5K. The right budget depends on your case-value math, not industry benchmarks." },
    { q: "What's a good cost-per-signed-case for legal PPC?", a: "Highly practice-area dependent. PI: $1,500–$5,000 per signed case is competitive in Colorado. Family law: $400–$1,200. Criminal defense: $300–$800. Estate planning: $200–$600. Anything outside these ranges is worth investigating." },
    { q: "Should I run LSAs or Search Ads first?", a: "If you can get the LSA badge (verification, license, insurance), start there — it's lower CPL than Search. Search Ads add scale and capture intent LSAs can't. Most successful Colorado firms run both." },
    { q: "Do you only serve Colorado Springs law firms?", a: "No — we serve attorneys across all of Colorado: Denver, Boulder, Fort Collins, Pueblo, Castle Rock, and the Front Range corridor." },
  ],
  serviceName: "Law Firm PPC in Colorado",
  serviceDescription:
    "Google Ads, Local Services Ads, and practice-area-specific PPC campaigns for law firms across Colorado.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/law-firm-ppc-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
